import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSelectChange } from '@angular/material/select';
import {
  CommonIP,
  CommonIPTrifold,
  DocumentSubType,
  DST_TRIFOLD_PROP_BY_IP_TYPE,
  FileWithProgress,
  IP,
  NestedSubType,
  ReferenceDetails,
  RelatedFileGroup,
  SubTypeByIpType,
  TRIFOLD_ENDPOINT_TO_LETTER_MAP,
  TrifoldDetailMap,
} from '@model/ftf-api.model';
import { AccountService } from '@service/account.service';
import { ApiService } from '@service/api.service';
import { LoggerService } from '@service/logger.service';
import { AddinSettings, SettingsService } from '@service/settings.service';
import { TrifoldItemEvent } from '@shared/components/ip-trifold/ip-trifold.component';
import { INVALID_FILENAME_CHARS_REGEX, MAX_FILE_TITLE_LENGTH, MAX_FILENAME_LENGTH, truncateFileName, truncateString } from '@utils/file-utils';
import { FormArrayDataSource } from '@utils/form-array.datasource';
import { SelectionFormArray } from '@utils/selection-form-array';
import { reductioUnum } from '@utils/validator-utils';
import { combineLatest, debounceTime, EmptyError, lastValueFrom, Observable, of, Subject, switchMap, takeUntil, throwError } from 'rxjs';
import { catchError, defaultIfEmpty, tap } from 'rxjs/operators';

import { maxByteLength, TableEvent, UploadTableFormControl, validateExtension } from './upload-table.model';
import { UploadTablePresentationComponent } from './upload-table.presentation.component';

@Component({
  selector: 'app-upload-table',
  standalone: true,
  imports: [UploadTablePresentationComponent],
  template: `
    <app-upload-table-presentation
      [form]="form"
      [relFileGroup]="relFileGroup"
      [displayedColumns]="displayedColumns"
      [dataSource]="files.controls"
      [selection]="selection"
      [subTypesByDocType]="subTypesByDocType"
      [supportedTrifolds]="supportedTrifolds"
      [item]="item"
      [ipType]="ipType"
      [identifier]="identifier"
      [files]="files"
      (openFTF)="openFTF.emit($event)"
      (openIPMS)="openIPMS.emit($event)"
      (closeTable)="onCloseTable($event)"
      (trifoldChange)="onTrifoldChange($event.event, $event.file)"
      (subTypeChange)="onSubTypeChange($event.event, $event.file)"
      (selectChange)="onSelectChange($event.event, $event.row)"
      (dateAppliedToAll)="applyDateToAll($event)"
      (subTypeAppliedToAll)="applySubTypeToAll($event)"
      (booleanAppliedToAll)="applyBooleanToAll($event.controlName, $event.value)"
      (trifoldAppliedToAll)="applyTrifoldToAll($event)"
      (allRowsToggled)="toggleAllRows($event)"
    ></app-upload-table-presentation>
  `,
})
export class UploadTableComponent implements OnDestroy, OnInit {
  @ViewChild(UploadTablePresentationComponent) presentationComponent!: UploadTablePresentationComponent;

  private apiService = inject(ApiService);
  private accountService = inject(AccountService);
  private settingsService = inject(SettingsService);
  private fb = inject(FormBuilder);
  private loggerService = inject(LoggerService);

  protected $ngDestroyed = new Subject<void>();

  @Input({ required: true }) form!: FormGroup<UploadTableFormControl>;
  @Input({ required: true }) relFileGroup!: RelatedFileGroup;
  @Input({ required: true }) allSubTypes!: SubTypeByIpType;
  @Input({ required: true }) trifoldMap!: TrifoldDetailMap;

  @Output() openFTF = new EventEmitter<TrifoldItemEvent>();
  @Output() openIPMS = new EventEmitter<TrifoldItemEvent>();
  @Output() closeTable = new EventEmitter<TableEvent>();
  @Output() filesAdded = new EventEmitter<void>();

  displayedColumns!: string[];
  files!: SelectionFormArray<FormGroup>;
  subTypesByDocType: NestedSubType = {};
  supportedTrifolds: { [key: string]: string } = {};
  item: CommonIP | null = null;
  selection!: SelectionModel<FormGroup>;

  private trifoldFiles: CommonIPTrifold | undefined;
  private debounceReferenceFns: { [key: string]: (...args: never[]) => Promise<ValidationErrors | null> } = {};
  private debounceFileDupeFns: { [key: string]: (...args: never[]) => Promise<ValidationErrors | null> } = {};
  private discoveredReferencesMap: Map<string, string | null> = new Map();
  private dataSource!: FormArrayDataSource<FileWithProgress>;
  private cachedTrifoldData: CommonIPTrifold | null = null;
  private trifoldDataPromise: Promise<CommonIPTrifold> | null = null;
  private settings: AddinSettings;

  constructor() {
    this.settings = this.settingsService.getSettings();
  }

  get ipType(): IP.TypeEnum {
    return this.form.get('ipType')?.value ?? 'Patent';
  }

  get identifier(): string {
    return this.form.get('identifier')?.value ?? '';
  }

  get relFileGroupId(): string {
    return this.relFileGroup.id;
  }

  ngOnInit(): void {
    if (!this.form) {
      throw new Error('form input not set');
    }
    this.initUploadTable().subscribe(() => {
      if (!(this.form.get('includeFiles')?.value ?? false)) {
        this.addFiles(this.relFileGroup.files);
      } else {
        this.filesAdded.emit();
        this.applyInitialFileSelection();

        const filesArray = this.form.get('files') as SelectionFormArray<FormGroup>;
        const autoRename = this.settingsService.getAutoRenameDuplicateFiles();
        this.applyValidationToExistingFiles(filesArray, this.ipType, autoRename);
      }
    });
  }

  ngOnDestroy(): void {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
    this.cachedTrifoldData = null;
    this.trifoldDataPromise = null;
  }

  private initUploadTable(): Observable<void> {
    this.files = this.form.get('files') as SelectionFormArray<FormGroup>;
    this.selection = this.files.selection;

    let row = 0;
    const autoRename = this.settingsService.getAutoRenameDuplicateFiles();

    this.dataSource = new FormArrayDataSource<FileWithProgress>(this.files, {
      convertTo: (r: FileWithProgress) => {
        return this.createFileFormGroup(r, ++row, this.ipType, autoRename);
      },
    });

    this.files.valueChanges.pipe(debounceTime(300), takeUntil(this.$ngDestroyed)).subscribe(() => {
      this.files.updateValueAndValidity({ emitEvent: true });
    });

    // if for whatever reason the identifier or ipType changes, update the displayed columns and file cache
    combineLatest({
      identifier: (this.form.get('identifier')?.valueChanges ?? of('')).pipe(debounceTime(300)),
      ipType: this.form.get('ipType')!.valueChanges.pipe(debounceTime(300)),
    })
      .pipe(takeUntil(this.$ngDestroyed))
      .subscribe(() => {
        this.updateProperties();
      });

    // update them initially
    this.updateProperties();

    return of(void 0);
  }

  private updateProperties(): void {
    const { ipType } = this;

    this.supportedTrifolds = {};
    // update sub types
    this.subTypesByDocType = this.allSubTypes[ipType];

    if (ipType !== 'Reference') {
      // update the item with common ip
      this.item = null;
      this.apiService.findCommonIP(this.identifier, ipType === 'CaseReference' ? 'Patent' : ipType).subscribe((res) => {
        this.item = res;
      });
    }

    if (ipType === 'Reference' || ipType === 'CaseReference') {
      // reference related columns
      this.displayedColumns = ['select', 'name', 'title', 'referenceType'];
    } else {
      // build map of ONLY the trifolds user can upload to
      const map = this.trifoldMap[ipType as IP.TrifTypeEnum];
      this.supportedTrifolds = Object.entries(map).reduce(
        (acc, [trifold, rec]) => {
          if (rec.upload) acc[trifold] = rec.label;
          return acc;
        },
        {} as { [key: string]: string }
      );
      // update the displayed columns for non references
      this.displayedColumns = ['select', 'name', 'title', 'trifold', 'date', 'subType', 'urgent', 'exportControl', 'docNotes', 'keywords'];
    }
  }

  public createFileFormGroup(file: FileWithProgress, row: number, ipType: string, autoRename: boolean): FormGroup {
    const reqByTriTypes = this.validateRequiredByType(['Patent', 'Trademark', 'Matter', 'CaseReference']);
    const truncatedFileName = truncateFileName(file.name, MAX_FILENAME_LENGTH, false, false);
    const fileName = this.settings.autoTruncateFileName ? truncatedFileName : file.name;
    // Resolve the final extension-adjusted title first, then truncate within the byte budget so the
    // extension adjustment can't push it back over the limit. Extension-bearing titles preserve the extension.
    const resolvedTitle = this.settings.includeFileExtension
      ? file.title.includes('/')
        ? file.title + '.' + (file.name.split('.').pop() ?? '')
        : file.name
      : file.title.replace(/\.[^/.]+$/, '');
    const title = this.settings.autoTruncateTitleName
      ? this.settings.includeFileExtension
        ? truncateFileName(resolvedTitle, MAX_FILE_TITLE_LENGTH, false, false)
        : truncateString(resolvedTitle, MAX_FILE_TITLE_LENGTH, false)
      : resolvedTitle;
    const fg = this.fb.nonNullable.group(
      {
        id: [file.id, [Validators.required]],
        titleTruncated: [resolvedTitle !== title],
        title: [title, [Validators.required, maxByteLength(MAX_FILE_TITLE_LENGTH), Validators.pattern(/^[^"\\]*$/)]],
        fileNameTruncated: [file.name !== fileName],
        name: [
          fileName,
          [
            Validators.required,
            maxByteLength(MAX_FILENAME_LENGTH),
            validateExtension(file.name.split('.').pop() ?? ''),
            (control: FormControl): ValidationErrors | null => {
              const isValid = !INVALID_FILENAME_CHARS_REGEX.test(control.value);
              return isValid ? null : { invalidName: true };
            },
          ],
        ],
        row: [row],
        ipType: [ipType],
        autoRename: [autoRename],
        isMainEmail: [file.isMainEmail],
        isInline: [file.isInline],
        originalFilename: file.name,
        originalTitle: file.title,
        dataFileError: [file.dataFile instanceof Boolean ? file.dataFile : null],
        dataFile: [file.dataFile, []],
        type: [file.type, [Validators.required]],
        trifold: [file.trifold, [reqByTriTypes, this.validateTrifold.bind(this)]],
        date: [file.date, [reqByTriTypes, Validators.required]],
        subType: [file.subType, [this.validateSubType.bind(this)]],
        urgent: [this.settings.markAsUrgent],
        exportControl: [file.exportControl],
        docNotes: [file.docNotes, [Validators.pattern('^[^{}]*$'), Validators.maxLength(1000), maxByteLength(1000)]],
        keywords: [file.keywords, [Validators.maxLength(20), maxByteLength(20)]],
        hasAttachments: [file.hasAttachments],
        entry: [file.entry, [Validators.required]],
        referenceType: [file.referenceType + '', [this.validateRequiredByType(['Reference', 'CaseReference'])]],
      },
      {
        validators: [this.validateFileData.bind(this)],
        asyncValidators: [this.asyncValidateReferenceNameDuplicate.bind(this), this.asyncValidateTrifoldNameDuplicate.bind(this)],
      }
    );
    return fg;
  }

  private applyValidationToExistingFiles(filesArray: SelectionFormArray<FormGroup>, ipType: IP.TypeEnum, autoRename: boolean): void {
    let row = 0;
    filesArray.controls.forEach((fileGroup) => {
      const file = fileGroup.value;
      const validatedGroup = this.createFileFormGroup(file, ++row, ipType, autoRename);
      fileGroup.markAllAsTouched();

      Object.keys(validatedGroup.controls).forEach((key) => {
        const control = fileGroup.get(key);
        const validatedControl = validatedGroup.get(key);
        if (control && validatedControl) {
          control.setValidators(validatedControl.validator);
          control.setAsyncValidators(validatedControl.asyncValidator);
          control.updateValueAndValidity();
        }
      });
      fileGroup.setValidators(validatedGroup.validator);
      fileGroup.setAsyncValidators(validatedGroup.asyncValidator);
      fileGroup.updateValueAndValidity();
    });
  }

  /**
   * Validates the required fields based on the given IP types.
   *
   * @param requiredByTypes - An array of IP types that require the field to be filled.
   * @returns A validation function that checks if the field is required based on the IP type.
   */
  private validateRequiredByType(requiredByTypes: IP.TypeEnum[] = []): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const type = this.ipType;
      const validation = requiredByTypes.includes(type) && !control.value ? { required: true } : null;
      return validation;
    };
  }

  private validateTrifold(control: FormControl): ValidationErrors | null {
    const type = this.ipType;
    const trifold = control.value;
    if (type === 'Reference' || type === 'CaseReference' || trifold == null) {
      return null;
    }
    return this.supportedTrifolds[trifold] ? null : { invalidTrifold: true };
  }

  /**
   * Validates the subtype control based on the selected IP type.
   *
   * @param control - The form control to validate.
   * @returns A `ValidationErrors` object if the control value is required, otherwise `null`.
   */
  private validateSubType(control: FormControl): ValidationErrors | null {
    const type = this.ipType;
    const dets = this.accountService.accountDetails;
    switch (type) {
      case 'Patent':
        return control.value ? null : dets?.displayPatTrifoldSubtype === true ? { required: true } : null;
      case 'Trademark':
        return control.value ? null : dets?.displayTmTrifoldSubtype === true ? { required: true } : null;
      case 'Matter':
        return control.value ? null : dets?.displayMatterTrifoldSubtype === true ? { required: true } : null;
      default:
        return null;
    }
  }

  private validateFileData(control: AbstractControl): ValidationErrors | null {
    if (control.get('dataFile')?.value === true) {
      // local files will have a dataFile value
      return null;
    } else if (control.get('dataFile')?.value === false || control.get('dataFileError')?.value === true) {
      return { dataFileError: true };
    } else if (control.get('dataFileError')?.value === null) {
      return { dataFileLoading: true };
    } else {
      return null;
    }
  }

  /**
   * Asynchronously validates various attributes using the API. Using Observables here is not ideal, as we are
   * validating a single field (title), but the FormGroup is running all validators on all fields. This is a workaround.
   * The alternative is to remove all validators and only run them on blur, or with a validate button at the top-most
   * level of the form.
   * @param {FormControl} control - The form control containing the reference name.
   * @returns {Promise<ValidationErrors | null>} - A promise that resolves to validation errors or null.
   */
  async asyncValidateReferenceNameDuplicate(control: AbstractControl): Promise<ValidationErrors | null> {
    const title: string = control.get('title')?.value ?? ''; //do NOT use const title in debouncedFunc below! Must be dynamic
    if (this.discoveredReferencesMap.has(title)) {
      const value: string | null = this.discoveredReferencesMap.get(title) as string | null;
      return value === null ? null : { duplicateReferencename: true };
    }
    //If you are here it is because there is no key in the map AKA typing a custom name

    const row = control.get('row')?.value;
    let result: ValidationErrors | null = null;
    const ipType = control.get('ipType')?.value ?? '';
    if (ipType === 'Reference' || ipType === 'CaseReference') {
      const debouncedFunc = this.getDebouncedFunction(
        this.debounceReferenceFns,
        row,
        () => this.collectReferenceInfo(control.get('title')?.value),
        1000,
        false,
        this
      );
      result = await debouncedFunc();
      result = result ?? null;
      const thisTitle = result ? result['duplicateReferencename'] : null;
      if (thisTitle) {
        this.discoveredReferencesMap.set(thisTitle, thisTitle);
      }
      return result;
    }
    return null;
  }

  async asyncValidateTrifoldNameDuplicate(control: AbstractControl): Promise<ValidationErrors | null> {
    const row = control.get('row')?.value;
    let result;
    const ipType = control.get('ipType')?.value ?? '';
    if (ipType === 'Patent' || ipType === 'Trademark' || ipType === 'Matter') {
      const debouncedFunc = this.getDebouncedFunction(
        this.debounceFileDupeFns,
        row,
        () => this.collectTrifoldFileInfo(control.get('name')?.value, control.get('trifold')?.value as IP.TrifoldEnum),
        1000,
        false,
        this
      );
      result = await debouncedFunc();
      if (result != null && control.get('autoRename')?.value) {
        const oldName = control.get('originalFilename')?.value;
        control.get('name')?.setValue(truncateFileName(oldName, MAX_FILENAME_LENGTH, false, true));
      }
      return result === undefined ? null : result;
    }
    return null;
  }

  /**
   * Collects reference information and checks for duplicate reference names.
   * @param {string} title - The title of the reference.
   * @returns {Promise<ValidationErrors | null>} - A promise that resolves to validation errors or null.
   */
  private collectReferenceInfo(title: string): Promise<ValidationErrors | null> {
    return lastValueFrom(
      this.apiService.findReferenceDetailsByName(title).pipe(
        switchMap((referenceDetails: ReferenceDetails) => {
          return of(referenceDetails.titleDisplay === title ? { duplicateReferencename: title } : null);
        }),
        defaultIfEmpty(null),
        catchError((error) => {
          if (error instanceof EmptyError) {
            return of(null);
          }
          return throwError(() => error);
        })
      )
    );
  }

  /**
   * Collects reference information and checks for duplicate filenames.
   * @param {string} fileName - The title of the reference.
   * @param {IP.TrifoldEnum} trifold - The trifold type.
   * @returns {Promise<ValidationErrors | null>} - A promise that resolves to validation errors or null.
   */
  public async collectTrifoldFileInfo(fileName: string, trifold: IP.TrifoldEnum): Promise<ValidationErrors | null> {
    try {
      const trifoldData = await this.getTrifoldData();
      // Check for duplicate files
      const files = trifoldData[trifold as keyof CommonIPTrifold];
      if (Array.isArray(files)) {
        const duplicate = files.some((file) => file.fileName === fileName);
        return duplicate ? { duplicateFilename: true } : null;
      }
      return null;
    } catch (error) {
      this.loggerService.error('Error checking for duplicate files:', error);
      return null;
    }
  }

  public getTrifoldData(): Promise<CommonIPTrifold> {
    // Return from cache if available
    if (this.cachedTrifoldData) {
      return Promise.resolve(this.cachedTrifoldData);
    }

    // Reuse in-flight request if one exists
    if (this.trifoldDataPromise) {
      return this.trifoldDataPromise;
    }

    // Make new request and cache the result
    this.trifoldDataPromise = lastValueFrom(
      this.apiService.findIPTrifold(this.identifier, this.ipType).pipe(
        tap((data) => {
          this.cachedTrifoldData = data;
          this.trifoldDataPromise = null;
        }),
        catchError((error) => {
          this.trifoldDataPromise = null;
          return throwError(() => error);
        })
      )
    );

    return this.trifoldDataPromise;
  }

  /**
   * Retrieves or creates a debounced function for a specific key. The `key` is the row we are working with. This allows
   * a field on a specific row to be debounced independently of other fields. This is because all fields are run the
   * first time the grid loads, and they all need to be debounced, but not as one group. The idea is that our FormGroup
   * is overzealous in running validators on all fields, so we need to debounce them as groups when we are validating
   * with the API. This is a workaround for that. The alternative is remove all validators and only run them on blur, or
   * with a validate button.
   * @param debounceMap - The map of debounced functions. The means by which we can debounce a function specific to a single key (like title).
   * @param uniqueKey - The key to use for the debounced function.
   * @param fn  - The function to debounce.
   * @param srcTimeout  - The timeout in milliseconds.
   * @param invokeAsap  - Controls whether the function should be executed immediately on the first call and then ignore subsequent calls until the timeout period has passed.
   * @param ctx - The context of the original function.
   * @returns The debounced function.
   */
  private getDebouncedFunction(
    debounceMap: {
      [key: string]: (...args: never[]) => Promise<ValidationErrors | null>;
    },
    uniqueKey: string,
    fn: (...args: never[]) => ValidationErrors | null,
    srcTimeout = 200,
    invokeAsap = false,
    ctx?: unknown
  ) {
    if (!debounceMap[uniqueKey]) {
      debounceMap[uniqueKey] = reductioUnum(fn, srcTimeout, invokeAsap, ctx);
    }
    return debounceMap[uniqueKey];
  }

  /**
   *
   * @param id
   * @returns
   */
  private findSubType(id: number): DocumentSubType | undefined {
    return Object.values(this.allSubTypes[this.ipType] ?? {})
      .flat()
      .find((s) => s.id === id);
  }

  /**
   * Adds the specified files to the upload table.
   * @param files The files to add.
   */
  public addFiles(files: FileWithProgress[]): void {
    if (this.ipType === 'Reference' || this.ipType === 'CaseReference') {
      const newFiles = files.filter((f) => !this.discoveredReferencesMap.has(f.title));
      if (newFiles.length > 0) {
        this.apiService.findReferencesByNames(newFiles.map((f) => f.title)).subscribe((res) => {
          res.forEach((value, key) => {
            this.discoveredReferencesMap.set(key, value);
          });
          this.dataSource.addRecords(files).subscribe(() => {
            if (this.presentationComponent?.table) {
              this.presentationComponent.table.renderRows();
            }
            this.applyInitialFileSelection();
          });
        });
      }
    } else {
      const settings = this.settingsService.getSettings();
      const ipType = this.ipType as IP.TrifTypeEnum;

      // Map each IP type to its configured default sub-type IDs from settings.
      // 'parent' applies to the first file (the email body), 'attachment' applies to all subsequent files.
      const subTypeMap: Partial<Record<IP.TypeEnum, { parent: string; attachment: string }>> = {
        [IP.TypeEnum.Patent]: { parent: settings.patentParentSubType, attachment: settings.patentAttachSubType },
        [IP.TypeEnum.Trademark]: { parent: settings.trademarkParentSubType, attachment: settings.trademarkAttachSubType },
        [IP.TypeEnum.Matter]: { parent: settings.matterParentSubType, attachment: settings.matterAttachSubType },
      };

      const { parent: parentSubType = '', attachment: attachmentSubType = '' } = subTypeMap[this.ipType] ?? {};

      // Builds the fields to apply to a file when a default sub-type is resolved.
      // Also applies the associated trifold if the sub-type maps to one that is supported for upload.
      const buildModifications = (subType: DocumentSubType): Partial<FileWithProgress> => {
        const result: Partial<FileWithProgress> = { subType: subType.id };
        // Subtypes show values like "filing", "correspondence".  We need to convert this to the letter
        const endpoint = subType[DST_TRIFOLD_PROP_BY_IP_TYPE[ipType]] as string | undefined;
        const property = endpoint ? TRIFOLD_ENDPOINT_TO_LETTER_MAP[endpoint] : undefined;
        if (property && this.supportedTrifolds[property]) {
          result.trifold = property as IP.TrifoldEnum;
        }
        return result;
      };

      // Resolve both sub-types once up front to avoid repeated lookups inside the map below.
      // Attachment sub-type is only relevant when there is more than one file (i.e. there are attachments).
      const resolvedParent = parentSubType ? this.findSubType(parseInt(parentSubType, 10)) : undefined;
      const resolvedAttachment = attachmentSubType && files.length > 1 ? this.findSubType(parseInt(attachmentSubType, 10)) : undefined;

      const processedFiles =
        resolvedParent || resolvedAttachment
          ? files.map((file, idx) => {
              // Skip files that already have a valid sub-type assigned.
              if (file.subType && this.findSubType(file.subType)) return file;
              // First file is the email body (parent), all others are attachments.
              const resolved = idx === 0 ? resolvedParent : resolvedAttachment;
              return resolved ? { ...file, ...buildModifications(resolved) } : file;
            })
          : files;

      this.dataSource.addRecords(processedFiles).subscribe(() => {
        if (this.presentationComponent?.table) {
          this.presentationComponent.table.renderRows();
        }
        this.applyInitialFileSelection();
      });
    }
  }

  private applyInitialFileSelection(): void {
    const settings = this.settingsService.getSettings();
    const filesArray = this.form.get('files') as SelectionFormArray<FormGroup>;

    this.selection.clear();

    filesArray.controls.forEach((fileGroup) => {
      const isMainEmail = fileGroup.get('isMainEmail')?.value === true;
      const isInline = fileGroup.get('isInline')?.value === true;

      const shouldSelect = isMainEmail || (isInline ? settings.markAllEmbeddedImagesForUpload : settings.markAllAttachmentsForUpload);

      if (shouldSelect) {
        this.selection.select(fileGroup);
      }
    });
  }

  toggleAllRows(event: MatCheckboxChange | null = null): void {
    if (event && !event.checked) {
      this.selection.clear();
      return;
    }
    this.selection.select(...(this.dataSource.data as unknown as FormGroup[]));
  }

  onSelectChange(event: MatCheckboxChange, row: FormGroup): void {
    if (event) {
      this.selection.toggle(row);
    }
  }

  onTrifoldChange($event: MatOptionSelectionChange, file: FormGroup): void {
    if ($event.source.selected) {
      if (this.settingsService.getAutoRenameDuplicateFiles() && this.trifoldFiles) {
        const trifold = $event.source.value as IP.TrifoldEnum;
        // if the trifold is the same as the current value, don't change the name
        if (trifold === file.get('trifold')?.value) return;
        const oldName = file.get('originalFilename')?.value;
        const addRandom = this.trifoldFiles && this.trifoldFiles[trifold].find((f) => f.fileName === oldName);
        const shouldChange = this.settingsService.getAutoRenameDuplicateFiles() && addRandom !== undefined;
        file.get('name')?.setValue(shouldChange ? truncateFileName(oldName, MAX_FILENAME_LENGTH, false, shouldChange) : oldName);
        file.get('name')?.updateValueAndValidity({ emitEvent: true });
      }
    }
  }

  onSubTypeChange(subType: DocumentSubType | null, file: FormGroup) {
    if (!subType || this.ipType === 'Reference' || this.ipType === 'CaseReference') return;
    const endpoint = subType[DST_TRIFOLD_PROP_BY_IP_TYPE[this.ipType]] as string | undefined;
    const property = endpoint ? TRIFOLD_ENDPOINT_TO_LETTER_MAP[endpoint] : undefined;
    if (property && this.supportedTrifolds[property]) {
      file.get('trifold')?.setValue(property as IP.TrifoldEnum);
    }
  }

  applyDateToAll(selectedDate: Date): void {
    this.files.controls.forEach((group) => {
      group.get('date')?.setValue(selectedDate);
    });
  }

  /**
   * Search for a trifold mapping associated with the given sub-type ID
   * @param subTypeId id of the subtype
   * @returns the trifold enum value if found, otherwise undefined
   */
  private determineTrifoldForSubTypeId(subTypeId: number): IP.TrifoldEnum | undefined {
    if (this.ipType === 'Reference' || this.ipType === 'CaseReference') return undefined;
    const subType = this.findSubType(subTypeId);
    if (!subType) return undefined;
    const endpoint = subType[DST_TRIFOLD_PROP_BY_IP_TYPE[this.ipType]] as string | undefined;
    const property = endpoint ? TRIFOLD_ENDPOINT_TO_LETTER_MAP[endpoint] : undefined;
    if (property && this.supportedTrifolds[property]) {
      return property as IP.TrifoldEnum;
    }
    return undefined;
  }

  applySubTypeToAll(event: MatSelectChange): void {
    const subType: number = event.value;
    if (subType) {
      const trifold = this.determineTrifoldForSubTypeId(subType);
      this.files.controls.forEach((group) => {
        group.get('subType')?.setValue(subType);
        if (trifold) {
          group.get('trifold')?.setValue(trifold);
        }
      });
    }
  }

  applyBooleanToAll(controlName: string, value: boolean): void {
    this.files.controls.forEach((group) => {
      group.get(controlName)?.setValue(value);
    });
  }

  applyTrifoldToAll(event: MatSelectChange): void {
    const trifold = event.value;
    if (trifold) {
      this.files.controls.forEach((group) => {
        group.get('trifold')?.setValue(trifold);
      });
    }
  }

  onCloseTable(event: TableEvent): void {
    this.closeTable.emit(event);
  }
}
